import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Star, Coffee, Wifi, Leaf, Wind, ArrowRight, Home, X, CheckCircle2, Search, Loader2, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = '/api';

// --- STRICT CATEGORY DEFINITIONS FOR AI ---
const categoryDefinitions = {
  'Luxury': "Focus exclusively on maximum comfort, lavish amenities, exclusivity, and premium world-class service.",
  'Eco-Friendly': "Focus exclusively on sustainability, off-grid living, nature integration, and environmental responsibility.",
  'Heritage': "Focus exclusively on historical architecture, restored palaces, forts, and places with deep cultural value.",
  'Homestay': "Focus exclusively on authentic stays hosted by local residents for a highly personal and cultural experience."
};

const LodgesPage = () => {
  const navigate = useNavigate();
  
  // States for filtering and AI Search
  const [activeCategory, setActiveCategory] = useState('All');
  const [starRating, setStarRating] = useState('All');
  const [searchDestination, setSearchDestination] = useState('');
  
  // States for rendering data and modals
  const [selectedLodge, setSelectedLodge] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lodges, setLodges] = useState([]);
  
  // --- NEW: Asynchronous Image Mapping State ---
  const [images, setImages] = useState({});

  // --- WIKIPEDIA IMAGE FETCHER ---
  const fetchWikiImage = async (title) => {
    if (!title) return null;
    try {
      const normalizeUrl = (src) => {
        if (!src) return null;
        if (src.startsWith('//')) return 'https:' + src;
        return src;
      };

      const pageImageForTitle = async (t, endpoint = 'https://en.wikipedia.org/w/api.php') => {
        const params = `origin=*&action=query&format=json&prop=pageimages&pithumbsize=800&titles=${encodeURIComponent(t)}`;
        const res = await fetch(`${endpoint}?${params}`).then(r => r.json()).catch(() => ({}));
        const pages = res.query?.pages;
        const pageId = pages ? Object.keys(pages)[0] : null;
        if (pageId && pageId !== '-1') {
          const page = pages[pageId];
          if (page.thumbnail?.source) return normalizeUrl(page.thumbnail.source);
        }
        return null;
      };

      let img = await pageImageForTitle(title, 'https://en.wikipedia.org/w/api.php');
      if (img) return img;

      img = await pageImageForTitle(title, 'https://en.wikivoyage.org/w/api.php');
      if (img) return img;

      const searchEndpoints = [
        'https://en.wikipedia.org/w/api.php',
        'https://en.wikivoyage.org/w/api.php'
      ];

      for (const endpoint of searchEndpoints) {
        try {
          const sParams = `origin=*&action=query&list=search&srsearch=${encodeURIComponent(title + ' India')}&srlimit=5&format=json`;
          const sRes = await fetch(`${endpoint}?${sParams}`).then(r => r.json()).catch(() => ({}));
          const hits = sRes.query?.search || [];
          if (hits.length > 0) {
            for (let i = 0; i < Math.min(hits.length, 3); i++) {
              const candidate = hits[i].title;
              const candImg = await pageImageForTitle(candidate, endpoint);
              if (candImg) return candImg;
            }
          }
        } catch (e) {
          // ignore and continue
        }
      }

      try {
        const sParams = `origin=*&action=query&list=search&srsearch=${encodeURIComponent(title)}&srlimit=5&format=json`;
        const sRes = await fetch(`https://en.wikipedia.org/w/api.php?${sParams}`).then(r => r.json()).catch(() => ({}));
        const hits = sRes.query?.search || [];
        for (let i = 0; i < Math.min(hits.length, 3); i++) {
          const candImg = await pageImageForTitle(hits[i].title, 'https://en.wikipedia.org/w/api.php');
          if (candImg) return candImg;
        }
      } catch (e) {
        // ignore
      }

      return null;
    } catch (e) {
      return null;
    }
  };

  // --- CORE AI SEARCH FUNCTION ---
  const executeSearch = useCallback(async (destinationToSearch, categoryToFetch, ratingToFetch) => {
    setIsLoading(true);
    
    const actualDest = destinationToSearch.trim() || 'India';
    const starStr = ratingToFetch !== 'All' ? `${ratingToFetch}-Star` : 'highly rated';

    const fetchCategoryAI = async (catName, count) => {
  const definition = categoryDefinitions[catName];

  const prompt = `You are a travel recommendations engine.

Return ONLY a valid JSON array.
No markdown.
No explanation.

Find real, verifiable ${starStr}-star ${catName} accommodations strictly inside ${actualDest}.

${definition}

Each object must follow this schema:

{
  "name": "Official property name",
  "neighborhood": "District or area within ${actualDest}",
  "desc": "Concise description of the property, max 120 characters",
  "highlights": ["feature 1", "feature 2"],
  "priceRange": "$ | $$ | $$$ | $$$$",
  "confidence": "high | medium"
}

Rules:
- Return only real properties.
- Never invent names.
- Return [] if none found.`;

  try {
    const response = await fetch(`/api/chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        messages: [{ role: "user", content: prompt }],
        max_tokens: 2048,
      }),
    });

    if (!response.ok) {
      console.error("API Error:", response.status);
      return [];
    }

    const chatData = await response.json();
    const rawText = chatData.reply || '';

    let recs;
    try {
      const startIdx = rawText.indexOf('[');
      const endIdx = rawText.lastIndexOf(']');
      if (startIdx === -1 || endIdx === -1) throw new Error("No JSON array found");
      recs = JSON.parse(rawText.substring(startIdx, endIdx + 1));
    } catch (err) {
      console.error("Invalid AI JSON:", rawText);
      return [];
    }

    if (!Array.isArray(recs) || !recs.length) return [];

    const seen = new Set();

    return recs
      .filter((rec) => {
        if (!rec.name) return false;

        const key = rec.name.toLowerCase().trim();

        if (seen.has(key)) return false;

        seen.add(key);
        return true;
      })
      .map((rec, idx) => ({
        id: `ai_${catName}_${idx}_${Date.now()}`,

        name: rec.name,

        location: rec.neighborhood || actualDest,

        category: catName,

        rating:
          ratingToFetch !== "All"
            ? Number(ratingToFetch)
            : Number((Math.random() * 0.8 + 4.2).toFixed(1)),

        desc: (rec.desc || "").substring(0, 120),

        fullDesc: rec.desc || "",

        highlights:
          Array.isArray(rec.highlights) && rec.highlights.length
            ? rec.highlights
            : [
                "Premium Amenities",
                "Excellent Service",
                "Great Location",
                "Free Wi-Fi",
              ],

        priceRange: rec.priceRange || "$$",

        confidence: rec.confidence || "medium",
      }));
  } catch (err) {
    console.error(`AI Fetch Error for ${catName}:`, err);
    return [];
  }
};

    try {
      if (categoryToFetch === 'All') {
        const results = await Promise.all([
          fetchCategoryAI('Luxury', 3),
          fetchCategoryAI('Eco-Friendly', 3),
          fetchCategoryAI('Heritage', 3),
          fetchCategoryAI('Homestay', 3)
        ]);
        setLodges(results.flat());
      } else {
        const results = await fetchCategoryAI(categoryToFetch, 12);
        setLodges(results);
      }
    } catch (err) {
      console.error("AI Recommendations Fetch Error:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // --- INITIAL LOAD ---
  useEffect(() => {
    executeSearch('India', 'All', 'All');
  }, [executeSearch]);

  // --- ASYNC IMAGE FETCHING LOOP ---
  useEffect(() => {
    const loadImages = async () => {
      // Loop through lodges and fetch images one by one to prevent UI blocking
      for (const lodge of lodges) {
        if (images[lodge.id] === undefined) {
          const img = await fetchWikiImage(lodge.name);
          setImages(prev => ({ ...prev, [lodge.id]: img || '/logo.png' }));
        }
      }
    };
    
    if (lodges.length > 0) {
      loadImages();
    }
  }, [lodges]); // Intentionally not including 'images' to prevent infinite loop

  // --- BUTTON HANDLERS ---
  const handleSearchClick = () => {
    executeSearch(searchDestination, activeCategory, starRating);
  };

  const handleClearSearch = () => {
    setSearchDestination('');
    executeSearch('India', activeCategory, starRating);
  };

  const handleCategoryClick = (cat) => {
    setActiveCategory(cat);
    executeSearch(searchDestination || 'India', cat, starRating);
  };

  const handleBookLodge = (e, lodgeData) => {
    e.stopPropagation();
    navigate('/plan', { state: { prefillLodge: lodgeData } });
  };

  const categoriesList = ['All', 'Luxury', 'Eco-Friendly', 'Heritage', 'Homestay'];
  
  // --- REUSABLE CARD COMPONENT ---
  const renderLodgeCard = (lodge) => (
    <div 
      key={lodge.id} 
      onClick={() => setSelectedLodge(lodge)}
      style={{ backgroundColor: 'white', borderRadius: '24px', overflow: 'hidden', border: '1px solid #e5e7eb', boxShadow: '0 10px 25px rgba(0,0,0,0.03)', transition: 'transform 0.3s', display: 'flex', flexDirection: 'column', cursor: 'pointer' }} 
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-8px)'; }} 
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <div style={{ height: '240px', position: 'relative', backgroundColor: '#f3f4f6' }}>
        <img 
          src={images[lodge.id] || '/logo.png'} 
          alt={lodge.name} 
          style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
          onError={(e) => { e.target.src = '/logo.png'; }}
        />
        <div style={{ position: 'absolute', top: '15px', left: '15px', backgroundColor: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(4px)', color: '#1f2937', padding: '6px 12px', borderRadius: '50px', fontSize: '12px', fontWeight: 'bold' }}>
          {lodge.category}
        </div>
        <div style={{ position: 'absolute', bottom: '-15px', right: '20px', backgroundColor: 'white', padding: '8px 12px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '4px', boxShadow: '0 4px 10px rgba(0,0,0,0.1)', fontWeight: 'bold', fontSize: '14px', color: '#111827' }}>
          <Star size={16} fill="#eab308" color="#eab308" /> {lodge.rating}
        </div>
      </div>
      <div style={{ padding: '30px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#6b7280', fontSize: '13px', fontWeight: '600', marginBottom: '8px', textTransform: 'capitalize' }}>
          <MapPin size={14} /> {lodge.location}
        </div>
        <h3 style={{ fontSize: '22px', fontWeight: 'bold', color: '#111827', marginBottom: '10px' }}>{lodge.name}</h3>
        <p style={{ fontSize: '15px', color: '#4b5563', lineHeight: '1.6', marginBottom: '20px', flex: 1 }}>{lodge.desc}</p>
        
        <div style={{ display: 'flex', gap: '15px', color: '#9ca3af', borderTop: '1px solid #f3f4f6', paddingTop: '20px', marginBottom: '20px' }}>
          <Wifi size={18} title="Free Wifi" />
          <Coffee size={18} title="Breakfast Included" />
          <Leaf size={18} title="Eco-friendly" />
          <Wind size={18} title="Air Conditioned" />
        </div>

        <button 
          onClick={(e) => handleBookLodge(e, lodge)} 
          style={{ width: '100%', padding: '14px', borderRadius: '12px', backgroundColor: '#111827', color: 'white', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }} 
          onMouseEnter={(e)=>e.currentTarget.style.backgroundColor='#1f2937'} onMouseLeave={(e)=>e.currentTarget.style.backgroundColor='#111827'}
        >
          Add to My Trip <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', fontFamily: 'system-ui, -apple-system, sans-serif', paddingBottom: '80px', overflowX: 'hidden', position: 'relative' }}>
      
      {/* HERO SECTION */}
      <div style={{ position: 'relative', height: '45vh', minHeight: '400px', backgroundImage: 'url(https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=1920&q=80)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(17, 24, 39, 0.4), rgba(17, 24, 39, 0.8))' }} />
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', width: '100%', padding: '0 20px', boxSizing: 'border-box' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', backgroundColor: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', padding: '8px 16px', borderRadius: '50px', color: 'white', fontSize: '14px', fontWeight: '600', marginBottom: '20px' }}>
            <Home size={16} /> Handpicked Stays
          </div>
          <h1 style={{ fontSize: '52px', fontWeight: '800', color: 'white', marginBottom: '15px', textShadow: '0 4px 12px rgba(0,0,0,0.4)', letterSpacing: '-1px' }}>Our Exclusive Stays</h1>
          <p style={{ fontSize: '18px', color: 'rgba(255,255,255,0.9)', maxWidth: '600px', margin: '0 auto' }}>From heritage palaces to hidden eco-retreats, discover real stays perfectly curated for your vibe.</p>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
        
        {/* SEARCH & RATING FILTER BAR */}
        <div style={{ display: 'flex', gap: '15px', marginTop: '-35px', marginBottom: '30px', justifyContent: 'center', flexWrap: 'wrap', position: 'relative', zIndex: 10 }}>
          
          <div style={{ display: 'flex', alignItems: 'center', backgroundColor: 'white', borderRadius: '50px', padding: '8px 20px', border: '1px solid #e5e7eb', boxShadow: '0 10px 25px rgba(0,0,0,0.08)', flex: '1', maxWidth: '400px' }}>
             <Search size={20} color="#9ca3af" />
             <input 
                type="text" 
                placeholder="Where do you want to stay?" 
                value={searchDestination}
                onChange={(e) => setSearchDestination(e.target.value)}
                onKeyDown={(e) => { if(e.key === 'Enter') handleSearchClick(); }}
                style={{ border: 'none', outline: 'none', padding: '8px 12px', width: '100%', fontSize: '15px', backgroundColor: 'transparent' }}
             />
             {searchDestination && (
               <button onClick={handleClearSearch} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#9ca3af', display: 'flex' }}>
                 <X size={18} />
               </button>
             )}
          </div>
          
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', backgroundColor: 'white', borderRadius: '50px', border: '1px solid #e5e7eb', boxShadow: '0 10px 25px rgba(0,0,0,0.08)', padding: '8px 20px', cursor: 'pointer', width: '220px' }}>
             <Star size={18} color="#eab308" fill={starRating !== 'All' ? "#eab308" : "none"} style={{ marginRight: '8px', zIndex: 2 }} />
             <select 
                value={starRating} 
                onChange={(e) => setStarRating(e.target.value)}
                style={{ border: 'none', outline: 'none', backgroundColor: 'transparent', fontSize: '15px', color: '#374151', cursor: 'pointer', WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none', paddingRight: '25px', position: 'relative', zIndex: 2, width: '100%' }}
             >
                <option value="All">All Star Ratings</option>
                <option value="5">5 Star Luxury</option>
                <option value="4">4 Star Premium</option>
                <option value="3">3 Star Standard</option>
                <option value="2">2 Star Budget</option>
                <option value="1">1 Star Basic</option>
             </select>
             <div style={{ position: 'absolute', right: '18px', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', pointerEvents: 'none', zIndex: 1 }}>
               <ChevronDown size={16} color="#9ca3af" />
             </div>
          </div>

          <button 
             onClick={handleSearchClick}
             style={{ padding: '12px 30px', borderRadius: '50px', backgroundColor: '#111827', color: 'white', fontSize: '15px', fontWeight: 'bold', border: 'none', cursor: 'pointer', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', transition: 'background 0.2s' }}
             onMouseEnter={(e)=>e.currentTarget.style.backgroundColor='#1f2937'} onMouseLeave={(e)=>e.currentTarget.style.backgroundColor='#111827'}
          >
             Search Hotels
          </button>
        </div>

        {/* CATEGORY FILTERS */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '50px' }}>
          {categoriesList.map(cat => (
            <button key={cat} onClick={() => handleCategoryClick(cat)} style={{ padding: '10px 22px', borderRadius: '50px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.3s', backgroundColor: activeCategory === cat ? '#16a34a' : 'white', color: activeCategory === cat ? 'white' : '#4b5563', border: activeCategory === cat ? '1px solid #16a34a' : '1px solid #e5e7eb', boxShadow: activeCategory === cat ? '0 8px 15px rgba(22, 163, 74, 0.2)' : '0 4px 6px rgba(0,0,0,0.02)' }}>
              {cat}
            </button>
          ))}
        </div>

        {/* LOADING & RENDER STATE */}
        {isLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '80px 0' }}>
            <Loader2 className="animate-spin" size={48} color="#16a34a" />
            <p style={{ marginTop: '20px', color: '#4b5563', fontSize: '16px', fontWeight: '500' }}>Curating the perfect stays for you...</p>
          </div>
        ) : (
          <>
            {lodges.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '30px' }}>
                {lodges.map(renderLodgeCard)}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '60px', backgroundColor: 'white', borderRadius: '24px', border: '1px dashed #d1d5db' }}>
                <Home size={48} color="#9ca3af" style={{ margin: '0 auto 15px' }} />
                <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#374151', marginBottom: '8px' }}>No matches found</h3>
                <p style={{ color: '#6b7280' }}>Try adjusting your filters or search destination.</p>
                <button onClick={handleClearSearch} style={{ marginTop: '20px', padding: '10px 20px', borderRadius: '50px', backgroundColor: '#f3f4f6', color: '#374151', border: 'none', cursor: 'pointer', fontWeight: '600' }}>
                   Clear Filters
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* --- DETAILED LODGE MODAL --- */}
      {selectedLodge && (
        <div 
          onClick={() => setSelectedLodge(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 100, backgroundColor: 'rgba(17, 24, 39, 0.8)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
        >
          <div 
            onClick={(e) => e.stopPropagation()} 
            style={{ backgroundColor: 'white', width: '100%', maxWidth: '800px', maxHeight: '90vh', borderRadius: '24px', overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative', boxShadow: '0 25px 50px rgba(0,0,0,0.3)', animation: 'slideUp 0.3s ease-out forwards' }}
          >
            <button onClick={() => setSelectedLodge(null)} style={{ position: 'absolute', top: '20px', right: '20px', background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white', zIndex: 10 }}>
              <X size={24} />
            </button>

            {/* Modal Header */}
            <div style={{ height: '300px', flexShrink: 0, position: 'relative', backgroundColor: '#f3f4f6' }}>
              <img 
                src={images[selectedLodge.id] || '/logo.png'} 
                alt={selectedLodge.name} 
                style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                onError={(e) => { e.target.src = '/logo.png'; }}
              />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(17, 24, 39, 0.95), transparent)' }} />
              <div style={{ position: 'absolute', bottom: '25px', left: '35px', right: '35px' }}>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  <span style={{ backgroundColor: '#16a34a', color: 'white', padding: '4px 10px', borderRadius: '50px', fontSize: '11px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px', textTransform: 'capitalize' }}><MapPin size={12} /> {selectedLodge.location}</span>
                  <span style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', padding: '4px 10px', borderRadius: '50px', fontSize: '11px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px' }}><Star size={12} fill="white" /> {selectedLodge.rating} Rating</span>
                </div>
                <h2 style={{ color: 'white', fontSize: '36px', fontWeight: '800', lineHeight: '1.2' }}>{selectedLodge.name}</h2>
              </div>
            </div>

            {/* Scrollable Details */}
            <div style={{ padding: '35px', overflowY: 'auto', backgroundColor: 'white', flex: 1 }}>
              <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#111827', marginBottom: '15px' }}>About the Property</h3>
              <p style={{ fontSize: '16px', color: '#4b5563', lineHeight: '1.7', marginBottom: '35px' }}>
                {selectedLodge.fullDesc}
              </p>

              <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#111827', marginBottom: '20px' }}>Property Highlights</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                {selectedLodge.highlights.map((highlight, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', backgroundColor: '#f9fafb', padding: '12px 16px', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                    <CheckCircle2 size={20} color="#16a34a" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span style={{ color: '#374151', fontSize: '15px', fontWeight: '500' }}>{highlight}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Modal Sticky Footer */}
            <div style={{ padding: '20px 35px', backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ color: '#4b5563', fontSize: '14px', fontWeight: '500' }}>Ready to stay here?</div>
              <button 
                onClick={(e) => handleBookLodge(e, selectedLodge)}
                style={{ padding: '14px 28px', borderRadius: '12px', backgroundColor: '#111827', color: 'white', fontSize: '15px', fontWeight: 'bold', border: 'none', cursor: 'pointer', transition: 'background 0.2s', display: 'flex', alignItems: 'center', gap: '8px' }}
                onMouseEnter={(e)=>e.currentTarget.style.backgroundColor='#1f2937'} onMouseLeave={(e)=>e.currentTarget.style.backgroundColor='#111827'}
              >
                Add to My Trip <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(40px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default LodgesPage;